import React, { useState, useCallback, useEffect } from "react";
import { Map, Marker, Popup } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapboxMap.css";
import RouteSource from "./RouteSource";

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1Ijoibnh0aW50ZXJhY3RpdmUiLCJhIjoiY21laXU1cHIxMDRzdDJrczY5cWhjOGhwMCJ9.RUKpP8_lBssBuBo8DhCmfA";

const MapboxMap = ({
  center = [-74.5, 40],
  zoom = 9,
  markers = [],
  onMarkerClick,
  onMapMove,
  showRoute = false,
  routeStart = null,
  routeEnd = null,
  style = { height: "100%", width: "100%" },
}) => {
  const [viewState, setViewState] = useState({
    longitude: center[0],
    latitude: center[1],
    zoom: zoom,
  });

  const [popupInfo, setPopupInfo] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [mapKey, setMapKey] = useState(0); // Key to force map re-render
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);

  // Sync center prop changes with viewState
  useEffect(() => {
    setViewState((prev) => ({
      ...prev,
      longitude: center[0],
      latitude: center[1],
    }));
  }, [center]);

  // Auto-retry effect when error occurs
  useEffect(() => {
    if (mapError && retryCount < 3 && !isAutoRetrying) {
      const timer = setTimeout(() => {
        // Auto-retry logic inline to avoid reference issues
        if (retryCount < 3) {
          console.log(`Auto-retrying map load... Attempt ${retryCount + 1}/3`);
          setIsAutoRetrying(true);
          setRetryCount((prev) => prev + 1);
          setMapError(null);
          setIsMapLoading(true);

          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, retryCount) * 1000;
          setTimeout(() => {
            setMapKey((prev) => prev + 1); // Force map re-render
            setIsAutoRetrying(false);
          }, delay);
        } else {
          console.log(
            "Max auto-retry attempts reached. Showing error to user."
          );
          setIsAutoRetrying(false);
        }
      }, 1000); // Wait 1 second before first auto-retry

      return () => clearTimeout(timer);
    }
  }, [mapError, retryCount, isAutoRetrying]);

  const onMove = useCallback(
    (evt) => {
      setViewState(evt.viewState);
      // Call onMapMove callback with new center coordinates
      if (onMapMove) {
        onMapMove([evt.viewState.longitude, evt.viewState.latitude]);
      }
    },
    [onMapMove]
  );

  const handleMarkerClick = useCallback(
    (event, marker) => {
      event.originalEvent.stopPropagation();
      // Only show popup for user marker, not treasure markers
      if (marker.id === "user") {
        setPopupInfo(null);
      } else {
        setPopupInfo(null);
      }
      if (onMarkerClick) {
        onMarkerClick(marker);
      }
    },
    [onMarkerClick]
  );

  const autoRetry = useCallback(() => {
    if (retryCount < 3) {
      console.log(`Auto-retrying map load... Attempt ${retryCount + 1}/3`);
      setIsAutoRetrying(true);
      setRetryCount((prev) => prev + 1);
      setMapError(null);
      setIsMapLoading(true);

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;
      setTimeout(() => {
        setMapKey((prev) => prev + 1); // Force map re-render
        setIsAutoRetrying(false);
      }, delay);
    } else {
      console.log("Max auto-retry attempts reached. Showing error to user.");
      setIsAutoRetrying(false);
    }
  }, [retryCount]);

  return (
    <Map
      {...viewState}
      onMove={onMove}
      style={style}
      mapStyle="mapbox://styles/mapbox/light-v11"
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      onLoad={() => {
        setIsMapLoading(false);
        setMapError(null);
        if (retryCount > 0) {
          console.log(
            `Map loaded successfully after ${retryCount} retry attempts`
          );
          setRetryCount(0); // Reset retry count on successful load
        } else {
          console.log("Map loaded successfully");
        }
      }}
      onError={(error) => {
        console.error("Mapbox error:", error);

        // Extract meaningful error information
        let errorInfo = error;
        if (error && typeof error === "object") {
          // Handle different types of Mapbox errors
          if (error.error) {
            errorInfo = {
              message: error.error.message || error.error.toString(),
              details:
                error.error.details || error.error.code || "Unknown error code",
              originalError: error,
            };
          } else if (error.message) {
            errorInfo = {
              message: error.message,
              details: error.type || error.name || "Unknown error type",
              originalError: error,
            };
          } else {
            errorInfo = {
              message: "Map loading failed",
              details: error.toString() || "Unknown error",
              originalError: error,
            };
          }
        }

        setMapError(errorInfo);
        setIsMapLoading(false);

        // Auto-retry on error (unless max attempts reached)
        if (retryCount < 3) {
          autoRetry();
        }
      }}
      key={mapKey} // Force re-render on retry
      // iOS-specific optimizations
      attributionControl={false}
      preserveDrawingBuffer={false}
      antialias={false}
      maxZoom={18}
      minZoom={3}
    >
      {/* NavigationControl removed to hide default Mapbox controls */}

      {markers.map((marker, index) => (
        <Marker
          key={marker.id || index}
          longitude={marker.position[0]}
          latitude={marker.position[1]}
          anchor="bottom"
          onClick={(event) => handleMarkerClick(event, marker)}
        >
          <div className={`marker ${marker.className || ""}`}>
            {marker.icon ? (
              <img src={marker.icon} alt={marker.title || "Marker"} />
            ) : (
              <div className="default-marker" />
            )}
          </div>
        </Marker>
      ))}

      {popupInfo && (
        <Popup
          anchor="top"
          longitude={popupInfo.position[0]}
          latitude={popupInfo.position[1]}
          onClose={() => setPopupInfo(null)}
          closeOnClick={false}
        >
          <div className="popup-content-data">
            <h3>{popupInfo.title}</h3>
            {popupInfo.description && <p>{popupInfo.description}</p>}
            {popupInfo.content && popupInfo.content}
          </div>
        </Popup>
      )}

      {showRoute && routeStart && routeEnd && (
        <RouteSource
          start={routeStart}
          end={routeEnd}
          onRouteFound={(routeData) => {
            console.log("Route found:", routeData);
          }}
        />
      )}

      {/* Loading overlay */}
      {isMapLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3498db",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 10px",
              }}
            ></div>
            <p>Loading map...</p>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {mapError && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div style={{ textAlign: "center", padding: "20px" }}>
            <h3>Map Loading Error</h3>
            <p>
              {retryCount >= 3
                ? "Maximum retry attempts reached. Please refresh the page or try again later."
                : isAutoRetrying
                ? `Automatically retrying... (${retryCount}/3)`
                : "Unable to load the map. Please check your internet connection."}
            </p>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "5px",
                padding: "10px",
                margin: "10px 0",
                fontSize: "12px",
                fontFamily: "monospace",
                textAlign: "left",
                maxHeight: "100px",
                overflow: "auto",
              }}
            >
              <strong>Error Details:</strong>
              <br />
              {(() => {
                if (!mapError) return "Unknown error";
                if (typeof mapError === "string") return mapError;
                if (mapError.message) return mapError.message;
                if (mapError.error) return mapError.error;
                if (mapError.details) return mapError.details;
                return JSON.stringify(mapError, null, 2);
              })()}
            </div>
            {isAutoRetrying && (
              <div
                style={{
                  padding: "15px 20px",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  marginTop: "15px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #3498db",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 10px",
                  }}
                ></div>
                <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                  Automatically retrying... Attempt {retryCount}/3
                </p>
              </div>
            )}
            {retryCount >= 3 && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  marginTop: "10px",
                  fontSize: "12px",
                }}
              >
                Refresh Page
              </button>
            )}
          </div>
        </div>
      )}
    </Map>
  );
};

export default MapboxMap;
